import {
  faUser,
  // faCartShopping,
  // faBullhorn,
  faMessages,
  faArrowRightFromBracket,
} from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useMemo } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Link } from '@/components/ui/link/link.component';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { formatCustomerPreferredName } from '@/utils/formatters/format-customer-preferred-name';
import { formatUserInitials } from '@/utils/formatters/format-user-initials';
import { authStore } from '@/store/auth.store';
import { useLogout } from '@/hooks/use-logout.hook';
import { phonePatientSac } from '@/utils/consts/sac.consts';
import { useWhatsappLinkWithAnalytics } from '@/hooks/use-whatsapp/use-whatsapp-link-with-analytics.hook';
import { FEATURE_MENU, type MenuFeatureName } from '@/feature-flags/consts/feature-menu.const';
import { useEnabledFeatures } from '@/feature-flags/hooks/use-feature-flag.hook';
import { logDebug } from '@/feature-flags/utils/logger';
import { getCorrelationContext } from '@/feature-flags/utils/correlation';

interface ProfileMenuItem {
  label: string;
  url: string;
  icon: IconDefinition;
  isNew: boolean;
  testId: string;
  target?: string;
  analyticsEvent?: string;
}

const MENU_FEATURES: MenuFeatureName[] = [
  'newAppointments',
  'newProfile',
  'newDependents',
  'newFavorites',
];

export function ProfileSheet() {
  const authenticatedUser = useStore(authStore, (state) => state.authenticatedUser);
  const logout = useLogout();
  const whatsappSacLink = useWhatsappLinkWithAnalytics(phonePatientSac, 'Olá! Sou paciente Medprev e preciso de suporte.');
  const whatsappSacUrl = whatsappSacLink.url;
  const { enabled: enabledFeatures } = useEnabledFeatures(MENU_FEATURES);

  const accountItems = useMemo(() => {
    const hiddenFeatures = MENU_FEATURES.filter((feature) => !enabledFeatures.includes(feature));

    logDebug('Menu items filtered', {
      event: 'menu_items_rendered',
      ...getCorrelationContext(),
      totalFeatures: MENU_FEATURES.length,
      visibleFeatures: enabledFeatures.map((feature) => FEATURE_MENU[feature].label),
      hiddenFeatures: hiddenFeatures.map((feature) => FEATURE_MENU[feature].label),
      source: 'ProfileSheet',
    });

    return enabledFeatures.map((feature): ProfileMenuItem => {
      const meta = FEATURE_MENU[feature];
      return {
        label: meta.label,
        url: meta.internalRoute,
        icon: meta.icon,
        isNew: false,
        testId: meta.testId,
      };
    });
  }, [enabledFeatures]);

  const profileItems = useMemo<{ category: string; items: ProfileMenuItem[] }[]>(() => {
    const sections = [
      {
        category: 'Conta',
        items: accountItems,
      },
      {
        category: 'Contato',
        items: [
          // { label: 'Feedback', url: '/feedback', icon: faBullhorn, isNew: false, testId: DataTestIdEnum.ITEM_FEEDBACK },
          {
            label: 'Fale com a gente',
            url: whatsappSacUrl,
            icon: faMessages,
            isNew: false,
            target: '_blank',
            testId: DataTestIdEnum.ITEM_HELP,
            analyticsEvent: AnalyticsEnum.CLICK_PROFILE_MENU_TALK_TO_US,
          },
        ],
      },
    ];

    return sections.filter((section) => section.items.length > 0);
  }, [accountItems, whatsappSacUrl]);

  const userItems = useMemo(() => {
    return {
      name: authenticatedUser ? formatCustomerPreferredName(authenticatedUser)?.split(' ')[0] : 'Visitante',
      thumbnail: null,
    };
  }, [authenticatedUser]);

  const initials = useMemo(
    () => (authenticatedUser ? formatUserInitials(formatCustomerPreferredName(authenticatedUser)) : ''),
    [authenticatedUser],
  );

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          data-testid={DataTestIdEnum.HEADER_USER_BUTTON}
          variant="secondary"
          color="primary"
          aria-label={initials ? `Perfil de ${userItems.name}` : 'Perfil'}
          className="w-8 h-8 min-w-8 p-0 rounded-full text-sm shrink-0"
        >
          {initials || <Icon icon={faUser} className="w-4 h-4" />}
        </Button>
      </SheetTrigger>
      <SheetContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        role="menu"
        aria-label="Sheet content for user profile"
        side="right"
        closingTagPosition="right"
        className="w-full sm:w-[25rem] text-gray-800 text-lg overflow-y-scroll px-0"
        data-testid={DataTestIdEnum.PROFILE_MENU_CONTENT}
      >
        <SheetHeader>
          <SheetTitle className="mb-8 mx-5">
            <div className="flex flex-row gap-4">
              <div className="text-gray-900 text-2xl text-left">
                <span>Olá,</span>
                {' '}
                <span className="font-bold">
                  {userItems.name}
                  !
                </span>
              </div>
            </div>
          </SheetTitle>
        </SheetHeader>

        <hr className="border border-gray-200 mb-6" />
        {profileItems.map((category) => (
          <div key={category.category} className="mt-6">
            <SheetDescription className="text-gray-600 uppercase text-lg font-light mb-3 mx-5">
              {category.category}
            </SheetDescription>
            <ul className="flex flex-col content-center !items-start">
              {category.items.map((item) => (
                <li
                  key={item.label}
                  className="flex flex-row  w-full focus-within:bg-gray-50 hover:bg-gray-50 text-gray-800 focus-within:text-success-700 hover:text-success-700 transition-all duration-300"
                >
                  <SheetClose asChild>
                    <Link
                      data-testid={item.testId}
                      prefetch={false}
                      role="menuitem"
                      href={item.url}
                      target={item.target}
                      data-analytics-event={item.analyticsEvent}
                      className="flex items-center gap-2 py-4 w-full px-5"
                    >
                      <Icon icon={item.icon} className="w-5 h-5" />
                      <span className="text-lg font-normal">{item.label}</span>
                      {item.isNew && (
                        <div className="bg-green-100 text-success-700 text-base font-semibold border border-success-400 self-center px-2 rounded h-fit">
                          Novo
                        </div>
                      )}
                    </Link>
                  </SheetClose>
                </li>
              ))}
            </ul>
            <hr className="border border-gray-200 mt-8 " />
          </div>
        ))}
        <SheetFooter
          className=" text-gray-800 !justify-start px-5 focus-within:bg-gray-50 focus-within:text-success-700 hover:bg-gray-50 hover:text-success-700 transition-all duration-300 mt-4"
        >
          <SheetClose
            data-testid={DataTestIdEnum.LOGOUT}
            onClick={logout}
            className="text-left py-4"
          >
            <Icon icon={faArrowRightFromBracket} className=" w-5 mr-2" />
            <span className="text-lg ">Sair</span>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
